import { ThemeProvider } from "@bonusx-global/bx-design-system";
import CssBaseline from "@mui/material/CssBaseline";
import StyledEngineProvider from "@mui/material/StyledEngineProvider";
import type { PropsWithChildren } from "react";
import { useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { NODE_ENV } from "../../../constants/env";
import useIsMobile from "../../../hooks/device/useIsMobile";
import { EmptyComponent } from "../../../legacy/components/common/EmptyComponent";
import { Modal } from "../../../legacy/components/poppers/Modal";
import citizenTheme, { citizenThemeWhiteBg } from "../../../legacy/styles/citizenTheme";
import DefaultLayoutDesktop from "./components/DefaultLayoutDesktop";
import DefaultLayoutMobile from "./components/DefaultLayoutMobile";

export interface DefaultLayoutProps extends PropsWithChildren<{}> {
  isLogoClickable?: boolean;
  variant?: "white" | "default";
}

function DefaultLayout({
  children,
  isLogoClickable = true,
  variant = "default",
}: Readonly<DefaultLayoutProps>): JSX.Element {
  const isMobile = useIsMobile();

  const handleErrorBoundary = useCallback(function handleError(e: Error) {
    if (NODE_ENV === "development") {
      console.error(e);
    } else {
      window.location.href = "/unexpected-error.html";
    }
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={variant === "white" ? citizenThemeWhiteBg : citizenTheme}>
        <CssBaseline />
        <Modal />
        <ErrorBoundary FallbackComponent={EmptyComponent} onError={handleErrorBoundary}>
          {isMobile ? (
            <DefaultLayoutMobile isLogoClickable={isLogoClickable}>{children}</DefaultLayoutMobile>
          ) : (
            <DefaultLayoutDesktop isLogoClickable={isLogoClickable}>{children}</DefaultLayoutDesktop>
          )}
        </ErrorBoundary>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default DefaultLayout;
